/**
 * "Share the memo" (DOP-619): Word or PDF, and nothing else to choose. The
 * file is the whole memo, questions for management included, with no source
 * references (DOP-644): provenance lives in the app.
 *
 * It replaces the Export dropdown. A format is not a menu item: bankers
 * choose between "I'll edit it before it goes out" and "send it as it is".
 *
 * There is no link to send: a memo opens only for the person who owns the
 * deal, so a teammate would get "not found". The file is what gets shared.
 */

import { CircleCheck, Download, Loader2, X } from "lucide-react";
import React, { useState } from "react";

import ModalDialog from "../ModalDialog";
import type { DealScreeningRun } from "../../types/dealScreening";

export type DealScreeningExportFormat = "docx" | "pdf";

export interface ShareMemoOptions {
	format: DealScreeningExportFormat;
}

export interface ShareMemoDialogProps {
	run: DealScreeningRun;
	downloading: boolean;
	onDownload: (options: ShareMemoOptions) => void;
	onClose: () => void;
}

const TITLE_ID = "share-memo-title";

const FORMATS: { format: DealScreeningExportFormat; title: string; hint: string }[] = [
	{ format: "docx", title: "Word (.docx)", hint: "Edit it before it goes out" },
	{ format: "pdf", title: "PDF", hint: "Ready to send as it is" },
];

const ShareMemoDialog: React.FC<ShareMemoDialogProps> = ({
	run,
	downloading,
	onDownload,
	onClose,
}) => {
	const [format, setFormat] = useState<DealScreeningExportFormat>("docx");
	const reviewed = run.status === "reviewed";

	return (
		<ModalDialog titleId={TITLE_ID} onClose={onClose} size="lg">
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-[22px] font-semibold text-[#111111]">
						Share the memo
					</h2>
					<p
						className={`mt-2 flex items-center gap-1.5 text-sm font-medium ${
							reviewed ? "text-[#067647]" : "text-[#5B6472]"
						}`}
					>
						{reviewed && <CircleCheck className="h-4 w-4" aria-hidden />}
						{reviewed
							? `Version ${run.version}, marked reviewed`
							: `Version ${run.version} is a draft. It has not been marked reviewed yet.`}
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="rounded-lg p-1.5 text-text-muted hover:bg-gray-100"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>

			<div role="radiogroup" aria-label="Format" className="mt-5 flex gap-2.5">
				{FORMATS.map((option) => {
					const on = option.format === format;
					return (
						<button
							key={option.format}
							type="button"
							role="radio"
							aria-checked={on}
							onClick={() => setFormat(option.format)}
							className={`flex flex-1 flex-col gap-1 rounded-xl px-4 py-3.5 text-left ${
								on
									? "border-2 border-[#004C4B] bg-[#f6fbfb] px-[15px] py-[13px]"
									: "border border-gray-200 bg-white hover:bg-gray-50"
							}`}
						>
							<span className="text-base font-semibold text-[#111111]">{option.title}</span>
							<span className="text-[12.5px] text-[#5B6472]">{option.hint}</span>
						</button>
					);
				})}
			</div>

			<div className="mt-6 flex items-center gap-2.5">
				<button
					type="button"
					onClick={() => onDownload({ format })}
					disabled={downloading || run.generating}
					className="ml-auto inline-flex h-10 items-center gap-2 rounded-lg bg-[#004C4B] px-[18px] text-base font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
				>
					{downloading ? (
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					) : (
						<Download className="h-4 w-4" aria-hidden />
					)}
					{downloading ? "Preparing…" : `Download ${format === "pdf" ? "PDF" : "Word"}`}
				</button>
			</div>
		</ModalDialog>
	);
};

export default ShareMemoDialog;
